import { useState, useCallback, useRef, useEffect } from "react";
import { GetMedicationRequestByOrganization } from "../GetMedicationRequestByOrganization";
import { extractPatients } from "../../condition/hooks/useConditionByOrganization";

interface UseMedicationRequestByOrganizationProps {
  /** Organization id sem prefixo, ex: 05490544000100 */
  organizationId: string;
  /** Status (ex: active, completed) */
  status?: string | string[];
  /** Data inicial YYYY-MM-DD (opcional) */
  startDate?: string;
  /** Data final YYYY-MM-DD (opcional) */
  endDate?: string;
  /** Quantidade de registros (default 100) */
  count?: number;
  /** Incluir Encounter no bundle */
  includeEncounter?: boolean;
  /** Indica se deve retornar apenas o resumo (opcional) */
  summary?: boolean;
  /** Incluir Patient no bundle */
  includePatient?: boolean;
}

interface UseMedicationRequestByOrganizationResult {
  data: {
    medicationRequests: fhir5.MedicationRequest[];
    total?: number;
    patients?: fhir5.Patient[];
  };
  loading: boolean;
  error: Error | null;
  fetchMedicationRequests: () => Promise<void>;
}

function extractMedicationRequests(
  bundle?: fhir5.Bundle
): fhir5.MedicationRequest[] {
  if (!bundle?.entry) return [];
  return bundle.entry
    .filter(
      (entry): entry is fhir5.BundleEntry<fhir5.MedicationRequest> =>
        entry.resource?.resourceType === "MedicationRequest"
    )
    .map((entry) => entry.resource!)
    .filter(
      (resource): resource is fhir5.MedicationRequest => resource !== undefined
    );
}

export function useMedicationRequestByOrganization(
  props: UseMedicationRequestByOrganizationProps
): UseMedicationRequestByOrganizationResult {
  const [data, setData] = useState<{
    medicationRequests: fhir5.MedicationRequest[];
    patients?: fhir5.Patient[];
    total?: number;
  }>({
    medicationRequests: [],
    patients: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const paramsRef = useRef(props);

  useEffect(() => {
    paramsRef.current = props;
  }, [props]);

  const fetchMedicationRequests = useCallback(async () => {
    const currentParams = paramsRef.current;
    if (!currentParams.organizationId) return;
    try {
      setLoading(true);
      setError(null);
      
      // Primeiro busca o count total
      const countResponse = await GetMedicationRequestByOrganization({
        ...currentParams,
        summary: true,
        count: 0,
      });
      
      const totalCount = countResponse.total || 0;
      console.log(`Total de MedicationRequest encontrados: ${totalCount}`);
      
      // Agora busca todos os registros com o count adequado
      const response = await GetMedicationRequestByOrganization({
        ...currentParams,
        count: totalCount > 0 ? totalCount : 1000,
        summary: false,
      });
      
      const medicationRequests = extractMedicationRequests(response);
      const patients = extractPatients(response);
      setData({ medicationRequests, patients, total: totalCount });
    } catch (err) {
      setError(
        err instanceof Error
          ? err
          : new Error("Erro ao buscar MedicationRequest")
      );
      console.error("useMedicationRequestByOrganization error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, loading, error, fetchMedicationRequests };
}
